/**
 * Utility for managing persistent bet storage across multiple game types
 */

const STORAGE_KEY = 'elotto_bet_cart';

// Add a debounced save function and in-memory cache
let cachedBets = null;
let saveTimeout = null;
const SAVE_DELAY = 300; // ms

/**
 * Get all bets from local storage or memory cache
 * @returns {Array} Array of bet objects with game type info
 */
export const getAllBets = () => {
  // Return from cache if available
  if (cachedBets !== null) {
    return cachedBets;
  }
  
  try {
    const storedBets = localStorage.getItem(STORAGE_KEY);
    cachedBets = storedBets ? JSON.parse(storedBets) : [];
    return cachedBets;
  } catch (error) {
    console.error('Error retrieving bets from storage:', error);
    cachedBets = [];
    return [];
  }
};

/**
 * Save all bets to local storage with debounce
 * @param {Array} bets Array of bet objects with game type info
 */
export const saveBets = (bets) => {
  // Update the cache immediately
  cachedBets = bets;
  
  // Clear any existing timeout
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  
  // Set a new timeout to save to localStorage
  saveTimeout = setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(bets));
    } catch (error) {
      console.error('Error saving bets to storage:', error);
    }
  }, SAVE_DELAY);
};

/**
 * Add a new bet to storage
 * @param {Object} bet Bet object with game info
 */
export const addBet = (bet) => {
  const currentBets = getAllBets();
  currentBets.push(bet);
  
  // Update the cache immediately
  cachedBets = currentBets;
  
  // Save to localStorage immediately without debounce for adding bets
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentBets));
  } catch (error) {
    console.error('Error saving bets to storage:', error);
  }
  
  return currentBets;
};

/**
 * Remove a bet from storage by its ID
 * @param {number} betId Index of the bet to remove
 */
export const removeBet = (betId) => {
  const currentBets = getAllBets();
  const updatedBets = currentBets.filter((_, index) => index !== betId);
  saveBets(updatedBets);
  return updatedBets;
};

/**
 * Clear all bets from storage
 */
export const clearBets = () => {
  cachedBets = [];
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Get bets for a specific game
 * @param {string} gameId Game ID to filter bets
 * @returns {Array} Array of bet objects for the specified game
 */
export const getBetsByGame = (gameId) => {
  const allBets = getAllBets();
  return allBets.filter(bet => bet.gameId === gameId);
};

/**
 * Calculate the total amount of all bets or bets for a specific game
 * @param {string} gameId Optional game ID to filter bets
 * @returns {number} Total bet amount
 */
export const calculateBetTotal = (gameId = null) => {
  const bets = gameId ? getBetsByGame(gameId) : getAllBets();
  return bets.reduce((sum, bet) => sum + (+bet.amount || 0), 0);
}; 